import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { sumOpeningBudgets } from '@/components/pages/job-posting/utils/channel-utils';
import { Separator } from '@/components/ui/separator';
import { Typography } from '@/components/ui/typography';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { cn } from '@/utils/classnames';

interface SummaryProps {
  recommendedBudget?: number; // budget per opening for the channel, use for Vonq channels
  recommendedDuration?: number; // duration for the channel, use for Vonq channels
  draftSelectedOpenings: Record<string, number | null>;
  isFree?: boolean;
}

export function Summary({
  recommendedBudget,
  recommendedDuration,
  draftSelectedOpenings,
  isFree,
}: SummaryProps) {
  const intl = useIntl();
  const { formatCurrency } = useCurrencySymbol();

  const numberOfOpenings = Object.keys(draftSelectedOpenings).length;
  const totalBudget = sumOpeningBudgets(draftSelectedOpenings);

  const displayBudget = useMemo(() => {
    if (isFree) {
      return intl.formatMessage({
        defaultMessage: 'Free',
        description: 'Free budget label on the channel card',
      });
    }

    if (recommendedBudget) {
      return formatCurrency(recommendedBudget);
    }

    return intl.formatMessage({
      defaultMessage: 'Set-up in next step',
      description: 'Label for the budget not set',
    });
  }, [formatCurrency, intl, isFree, recommendedBudget]);

  return (
    <div className="border-fx-neutral-200 bg-fx-neutral-50 flex flex-col gap-4 border-t p-4">
      {/* Budget per opening */}
      <div className="flex items-center justify-between">
        <Typography variant="body1" className="text-fx-neutral-600">
          <FormattedMessage
            defaultMessage="Budget per opening:"
            description="Label for the budget per opening"
          />
        </Typography>
        <Typography variant="body1" className="text-fx-neutral-600">
          {displayBudget}
        </Typography>
      </div>

      {/* Number of openings */}
      <div className="flex items-center justify-between">
        <Typography variant="body1" className="text-fx-neutral-600">
          <FormattedMessage
            defaultMessage="Number of openings:"
            description="Label for the number of openings"
          />
        </Typography>
        <Typography variant="body1" className="text-fx-neutral-600">
          {numberOfOpenings || '-'}
        </Typography>
      </div>

      {/* Total budget */}
      {totalBudget ? (
        <div className="flex items-center justify-between">
          <Typography variant="body1" className="font-bold">
            <FormattedMessage
              defaultMessage="Total budget:"
              description="Label for the total budget"
            />
          </Typography>
          <Typography variant="body1" className="font-bold">
            {formatCurrency(totalBudget)}
          </Typography>
        </div>
      ) : null}

      <Separator />

      {/* Duration */}
      <div className="flex items-center justify-between">
        <Typography variant="body1" className="font-bold">
          <FormattedMessage
            defaultMessage="Duration"
            description="Label for the duration"
          />
        </Typography>
        <Typography
          variant="body1"
          className={cn(
            recommendedDuration ? 'font-bold' : 'text-fx-neutral-600',
          )}
        >
          {recommendedDuration ? (
            <FormattedMessage
              defaultMessage="{duration} days"
              values={{ duration: recommendedDuration }}
              description="Duration label, e.g. '30 days'"
            />
          ) : (
            <FormattedMessage
              defaultMessage="Set-up in next step"
              description="Label for the budget not set"
            />
          )}
        </Typography>
      </div>
    </div>
  );
}
